// One-time Notes-history import (idea.md Phase 4). Marko pastes his Apple Notes
// wall-of-text into Claude/Cowork, which structures it into the JSON below; this
// ingests it into IndexedDB (the source of truth), from where it syncs to Notion.
//
// No fabricated data: an exercise that does not match the catalog is reported and
// skipped, never invented. All weights are TOTAL kg (the import prompt instructs
// the per-hand -> total doubling).

import type { Exercise, Session, SetLog, ExerciseEntry } from '../../domain/types';
import { saveSession } from '../../db/repo';
import { DEFAULT_GYM_ID } from '../../domain/gym';

export interface ImportSet {
  weightKg: number;
  reps: number;
  rir?: number | null;
  warmup?: boolean;
}
export interface ImportExercise {
  exerciseId?: string;
  exercise?: string;
  elbowFeel?: 'none' | 'aware' | 'pain' | null;
  forearmFeel?: 'none' | 'aware' | 'pain' | null;
  grip?: string | null;
  note?: string;
  sets: ImportSet[];
}
export interface ImportSession {
  date: string;
  routine?: string;
  bodyweightKg?: number | null;
  note?: string;
  exercises: ImportExercise[];
}
export interface ImportFile {
  sessions: ImportSession[];
}

export interface ImportResult {
  importedSessions: number;
  importedEntries: number;
  warnings: string[];
}

function normalize(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '');
}

function slug(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 24);
}

function resolveExercise(spec: ImportExercise, exById: Map<string, Exercise>, byName: Map<string, Exercise>): Exercise | null {
  if (spec.exerciseId && exById.has(spec.exerciseId)) return exById.get(spec.exerciseId)!;
  if (spec.exercise) {
    const hit = byName.get(normalize(spec.exercise));
    if (hit) return hit;
  }
  return null;
}

function toSetLogs(sets: ImportSet[]): SetLog[] {
  return sets.map((s, i) => {
    const warmup = s.warmup === true;
    const rir = warmup ? null : s.rir ?? 1;
    return {
      setNumber: i + 1,
      isWarmup: warmup,
      weightKg: Number(s.weightKg) || 0,
      reps: Math.round(Number(s.reps) || 0),
      rir,
      isFailure: rir === 0,
    };
  });
}

export function validateImport(text: string): { data: ImportFile | null; error: string | null } {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (e) {
    return { data: null, error: `Not valid JSON: ${String(e)}` };
  }
  const obj = parsed as ImportFile;
  if (!obj || !Array.isArray(obj.sessions)) {
    return { data: null, error: 'Expected an object with a "sessions" array.' };
  }
  for (const s of obj.sessions) {
    if (!s.date || !/^\d{4}-\d{2}-\d{2}$/.test(s.date)) return { data: null, error: `Session missing valid date (YYYY-MM-DD): ${JSON.stringify(s).slice(0, 80)}` };
    if (!Array.isArray(s.exercises)) return { data: null, error: `Session ${s.date} has no exercises array.` };
  }
  return { data: obj, error: null };
}

export async function importHistory(data: ImportFile, exById: Map<string, Exercise>): Promise<ImportResult> {
  const byName = new Map<string, Exercise>();
  for (const ex of exById.values()) byName.set(normalize(ex.name), ex);

  const warnings: string[] = [];
  let importedSessions = 0;
  let importedEntries = 0;

  for (let i = 0; i < data.sessions.length; i++) {
    const spec = data.sessions[i];
    const entries: ExerciseEntry[] = [];
    let order = 0;
    for (const exSpec of spec.exercises) {
      const ex = resolveExercise(exSpec, exById, byName);
      if (!ex) {
        warnings.push(`${spec.date}: no catalog match for "${exSpec.exercise ?? exSpec.exerciseId}" - skipped`);
        continue;
      }
      const sets = toSetLogs(exSpec.sets ?? []);
      if (sets.length === 0) continue;
      entries.push({
        exerciseId: ex.id,
        order: order++,
        substitutedFrom: null,
        sets,
        elbowFeel: exSpec.elbowFeel ?? null,
        forearmFeel: exSpec.forearmFeel ?? null,
        gripVariant: (exSpec.grip as ExerciseEntry['gripVariant']) ?? null,
        note: exSpec.note ?? '',
      });
      importedEntries++;
    }
    if (entries.length === 0) {
      warnings.push(`${spec.date}: no matched exercises - session skipped`);
      continue;
    }
    const iso = new Date(spec.date + 'T12:00:00').toISOString();
    const session: Session = {
      id: `import-${spec.date}-${slug(spec.routine ?? 'session')}-${i}`,
      date: spec.date,
      // Imported Notes history predates gym tracking; it's all home-gym work.
      gymId: DEFAULT_GYM_ID,
      routineId: null,
      routineLabel: spec.routine ?? 'Imported session',
      bodyweightKg: spec.bodyweightKg ?? null,
      sessionNote: spec.note ? `${spec.note} (imported)` : 'Imported from Notes history',
      entries,
      createdAt: iso,
      updatedAt: iso,
      finishedAt: iso,
      coaching: null,
      suggestedAdjustments: null,
      coachedAt: null,
    };
    await saveSession(session);
    importedSessions++;
  }

  return { importedSessions, importedEntries, warnings };
}
